import { CaretRightOutlined } from '@ant-design/icons'
import { NetworkStatus } from '@apollo/client'
import { Avatar, Button, List, Spin } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Timestamp, UserHoverCard } from '@/components'
import { useGetDescendantPostQuery } from '@/generated/graphql'
import { routes } from '@/routes'

import s from './share-list.module.less'

export interface ShareListProps {
  postId: number
  showLoadmore?: boolean
  toggle: () => void
}

export const ShareList: React.FC<ShareListProps> = ({ postId, showLoadmore = true, toggle }) => {
  const { t } = useTranslation()
  const history = useHistory()

  const fetchCount = 8
  const pagination = { last: fetchCount }
  const { data, error, fetchMore, networkStatus } = useGetDescendantPostQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    variables: {
      postId,
      pagination,
    },
  })

  if (error) {
    return null
  }

  const { posts, pageInfo } = (() => {
    if (!data || !data.post.descendantPosts) {
      return {
        users: [],
        pageInfo: {
          startCursor: null,
          endCursor: null,
          hasNextPage: null,
          hasPreviousPage: null,
        },
      }
    }
    return {
      posts: data.post.descendantPosts.edges.slice(),
      pageInfo: data.post.descendantPosts.pageInfo,
    }
  })()

  const onLoadmore = () => {
    fetchMore({
      variables: {
        pagination: {
          ...pagination,
          ...{ before: pageInfo.startCursor },
        },
      },
    })
  }

  const footer = () => {
    if (networkStatus === NetworkStatus.fetchMore) {
      return <Spin />
    }
    if (showLoadmore && pageInfo.hasPreviousPage) {
      return (
        <Button shape='round' onClick={onLoadmore}>
          {t('groups.grouplist.loadmore')}
        </Button>
      )
    }
  }

  const onClick = (groupId: any, userEmail: string) => {
    toggle()
    history.push(groupId ? routes.groups.page(groupId) : routes.users.page(userEmail))
  }

  return (
    <List
      loading={networkStatus === NetworkStatus.loading}
      footer={footer()}
      dataSource={posts}
      split={false}
      className={s.list}
      renderItem={({ node: p }) => (
        <List.Item key={p.postId} className={s.listItem} onClick={() => onClick(p.group?.groupId, p.creator!.email)}>
          <List.Item.Meta
            avatar={<Avatar src={p.creator!.avatarUrl} />}
            title={
              <div className={s.meta}>
                <UserHoverCard email={p.creator!.email}>
                  <div className={s.name}>{p.creator!.name}</div>
                </UserHoverCard>
                <div>
                  <CaretRightOutlined />
                </div>
                <div className={s.target}>{p.group ? p.group.name : t('post.shares.personalTime')}</div>
              </div>
            }
            description={<Timestamp time={p.createdAt} />}
          />
        </List.Item>
      )}
    />
  )
}
